/**
 * Session 驗證工具
 * 用於在功能頁面初始化時驗證 session 有效性
 */

import { authService } from '../api/authService';

/**
 * 驗證 Session 有效性
 *
 * 流程：
 * 1. 檢查 localStorage 是否有 txn_token (session_id)
 * 2. 如果沒有 → 導向登入頁
 * 3. 如果有 → 呼叫後端 API 驗證 session 是否有效
 * 4. 有效 → 繼續執行
 * 5. 無效 → 清除 token 並導向登入頁
 *
 * @param pageName 頁面名稱（用於 console log）
 * @returns Promise<boolean> - true 表示 session 有效，false 表示無效（已導向登入頁）
 */
export const validateSession = async (pageName: string): Promise<boolean> => {
  try {
    // 步驟 1: 檢查 localStorage 是否有 txn_token
    const txnToken = localStorage.getItem('txn_token');
    const sessionId = localStorage.getItem('session_id') || localStorage.getItem('access_token');

    if (!txnToken || !sessionId) {
      console.error(`[${pageName}] No token found in localStorage, redirecting to login...`);
      authService.clearToken();
      window.location.href = '/login';
      return false;
    }

    // 步驟 2: 呼叫後端 API 驗證 session 有效性
    console.log(`[${pageName}] Verifying session...`);
    const result = await authService.verifySession();

    if (!result.valid) {
      console.error(`[${pageName}] Session is invalid, redirecting to login...`);
      authService.clearToken();
      window.location.href = '/login';
      return false;
    }

    console.log(`[${pageName}] Session is valid:`, {
      session_id: result.session_id,
      user: result.username,
      account: result.account
    });

    return true;
  } catch (error: any) {
    console.error(`[${pageName}] Session validation failed:`, error);

    // 401 錯誤表示 session 無效或過期
    if (error.response?.status === 401) {
      console.error(`[${pageName}] Session expired or invalid, redirecting to login...`);
      authService.clearToken();
      window.location.href = '/login';
      return false;
    }

    // 其他錯誤（如網路錯誤）也導向登入頁
    console.error(`[${pageName}] Unexpected error during validation, redirecting to login...`);
    authService.clearToken();
    window.location.href = '/login';
    return false;
  }
};
